import React, { MutableRefObject, useRef, useState, useEffect, useCallback, useMemo } from "react";
import { useSafeGLTF } from "../../../../utils/safeGLTFLoader";
import { TireRackItemI } from "../../../../store/types/store";
import { useCurrent } from "../../../../hooks/useCurrent";
import {
    BoxBufferGeometry,
    BoxHelper,
    Group,
    Mesh,
    MeshStandardMaterial,
    Vector3,
    Color,
    MeshPhysicalMaterial,
    Object3D,
} from "three";
import { useStoreRoom } from "../../../../store";
import { useThree } from "@react-three/fiber";
import TireRackDimensions from "./TireRackDimensions";
import { useColors } from "../../../../hooks/useColors";
// @ts-ignore
import { clone } from "three/examples/jsm/utils/SkeletonUtils.js";

import shallow from "zustand/shallow";
import { BBoxItem, getDecorBboxCollection } from "../../DecorsList/components/getDecorBboxCollection";
import { createBboxMesh } from "./createBboxMesh";
import { calcCollisions } from "./calcCollisions";
import { UniversalDraggable } from "../../UniversalDraggable";
import { Box3 } from "three";

interface TireRackObjectProps {
    tireRack: TireRackItemI;
    moveCameraEnabled: MutableRefObject<boolean>;
}

const setCursor = (cursor: CSSStyleDeclaration["cursor"]) => {
    document.body.style.cursor = cursor;
};

const DEFAULT_TIRE_RACK_SKIP_TOP_METAL_PARTS = 1;

const getVertexKey = (position: any, vertexIndex: number) => {
    const precision = 5;

    return [
        position.getX(vertexIndex).toFixed(precision),
        position.getY(vertexIndex).toFixed(precision),
        position.getZ(vertexIndex).toFixed(precision),
    ].join("|");
};

const isFaneraMaterial = (material: unknown) => {
    const materialName = (material as { name?: string })?.name;

    return Boolean(materialName?.trim().toLowerCase().includes("fanera"));
};

const hasFaneraMaterial = (object: Object3D) => {
    let result = false;

    object.traverse((child: Object3D) => {
        if (result || !(child instanceof Mesh) || !child.material) return;

        const materialList = Array.isArray(child.material) ? child.material : [child.material];

        result = materialList.some(isFaneraMaterial);
    });

    return result;
};

const splitMetalMaterialByConnectedParts = (
  mesh: Mesh,
  sourceMaterial: MeshPhysicalMaterial,
  modelName: string,
  wireframe: boolean
) => {
    const skipTopParts = DEFAULT_TIRE_RACK_SKIP_TOP_METAL_PARTS;

    if (skipTopParts <= 0) {
        return false;
    }

    const geometry = mesh.geometry;
    const position = geometry.attributes.position;

    if (!position) {
        return false;
    }

    const index = geometry.index;
    const triangleCount = index ? index.count / 3 : position.count / 3;

    if (!triangleCount) {
        return false;
    }

    const parent = Array.from({ length: triangleCount }, (_, triangleIndex) => triangleIndex);

    const find = (value: number): number => {
        if (parent[value] !== value) {
            parent[value] = find(parent[value]);
        }

        return parent[value];
    };

    const union = (a: number, b: number) => {
        const rootA = find(a);
        const rootB = find(b);

        if (rootA !== rootB) {
            parent[rootB] = rootA;
        }
    };

    const getTriangleVertexIndexes = (triangleIndex: number) => {
        if (index) {
            return [
                index.getX(triangleIndex * 3),
                index.getX(triangleIndex * 3 + 1),
                index.getX(triangleIndex * 3 + 2),
            ];
        }

        return [triangleIndex * 3, triangleIndex * 3 + 1, triangleIndex * 3 + 2];
    };

    const vertexToTriangles = new Map<string, number[]>();

    for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex++) {
        const vertexIndexes = getTriangleVertexIndexes(triangleIndex);

        vertexIndexes.forEach((vertexIndex) => {
            const key = getVertexKey(position, vertexIndex);
            const linkedTriangles = vertexToTriangles.get(key) ?? [];

            linkedTriangles.forEach((linkedTriangleIndex) => {
                union(triangleIndex, linkedTriangleIndex);
            });

            linkedTriangles.push(triangleIndex);
            vertexToTriangles.set(key, linkedTriangles);
        });
    }

    type MetalPart = {
        root: number;
        triangleIndexes: number[];
        minY: number;
        maxY: number;
        centerY: number;
        materialIndex: number;
    };

    const partsMap = new Map<number, MetalPart>();

    for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex++) {
        const root = find(triangleIndex);
        const vertexIndexes = getTriangleVertexIndexes(triangleIndex);
        const ys = vertexIndexes.map((vertexIndex) => position.getY(vertexIndex));
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);

        let part = partsMap.get(root);

        if (!part) {
            part = {
                root,
                triangleIndexes: [],
                minY,
                maxY,
                centerY: 0,
                materialIndex: 1,
            };

            partsMap.set(root, part);
        }

        part.triangleIndexes.push(triangleIndex);
        part.minY = Math.min(part.minY, minY);
        part.maxY = Math.max(part.maxY, maxY);
    }

    const parts = Array.from(partsMap.values())
      .map((part) => ({
          ...part,
          centerY: (part.minY + part.maxY) / 2,
      }))
      .sort((a, b) => b.centerY - a.centerY);

    parts.forEach((part, partIndexFromTop) => {
        part.materialIndex = partIndexFromTop < skipTopParts ? 0 : 1;
    });

    const tableMaterial = sourceMaterial.clone();
    tableMaterial.color.copy(new Color(0xFFFFFF));
    tableMaterial.roughness = 0.8;
    tableMaterial.metalness = 0.5;
    tableMaterial.transparent = true;
    tableMaterial.opacity = wireframe ? 0 : 1;
    tableMaterial.wireframe = wireframe;

    const supportMaterial = sourceMaterial.clone();
    supportMaterial.color = new Color(0x797b7a);
    supportMaterial.roughness = 0.8;
    supportMaterial.metalness = 0.5;
    supportMaterial.transparent = true;
    supportMaterial.opacity = wireframe ? 0 : 1;
    supportMaterial.wireframe = wireframe;

    const triangleMaterialIndexes = new Array<number>(triangleCount).fill(1);

    parts.forEach((part) => {
        part.triangleIndexes.forEach((triangleIndex) => {
            triangleMaterialIndexes[triangleIndex] = part.materialIndex;
        });
    });

    geometry.clearGroups();

    let groupStart = 0;
    let currentMaterialIndex = triangleMaterialIndexes[0];

    for (let i = 1; i <= triangleMaterialIndexes.length; i++) {
        const nextMaterialIndex = triangleMaterialIndexes[i];

        if (nextMaterialIndex !== currentMaterialIndex || i === triangleMaterialIndexes.length) {
            geometry.addGroup(groupStart * 3, (i - groupStart) * 3, currentMaterialIndex);

            groupStart = i;
            currentMaterialIndex = nextMaterialIndex;
        }
    }

    mesh.material = [tableMaterial, supportMaterial];

    return true;
};

const TireRackObject: React.FC<TireRackObjectProps> = React.memo((props) => {
    const { tireRack, moveCameraEnabled } = props;
    const { setCurrentEditItem, currentEditItem } = useCurrent();
    const { colorsShade, colorsItems } = useColors();

    const groupRef = useRef<Group | null>(null);
    const draggableRef = useRef<Group | null>(null);
    const bBoxRef = useRef<Mesh<BoxBufferGeometry, MeshStandardMaterial> | null>(null);
    const boxHelperRef = useRef<BoxHelper | null>(null);
    const decorBboxCollectionRef = useRef<BBoxItem[]>();

    const [visible, setVisible] = useState(false);
    const [isEmptyAabb, setIsEmptyAabb] = useState(true);
    const [isWireframe, setIsWireframe] = useState(false);

    const { scene } = useThree();
    const updateTireRack = useStoreRoom((state) => state.updateTireRack, shallow);

    const roomLength = useStoreRoom.getState().length / 1000;
    const roomHeight = useStoreRoom.getState().height / 1000;
    const roomDepth = useStoreRoom.getState().depth / 1000;

    const { scene: tireRackScene } = useSafeGLTF(tireRack.config.url);

    useEffect(() => {
        if (!tireRackScene) return;

        const materials = new Map<string, any>();

        tireRackScene.traverse((child: Object3D) => {
            if (!(child instanceof Mesh) || !child.material) return;

            const materialList = Array.isArray(child.material) ? child.material : [child.material];

            materialList.forEach((material: any, materialIndex) => {
                const key = [
                    tireRack.config.model_name,
                    tireRack.name,
                    child.name || "mesh-without-name",
                    material.name || "material-without-name",
                    materialIndex,
                ].join(" | ");

                const bbox = new Box3().setFromObject(child);
                const size = new Vector3();
                const center = new Vector3();

                bbox.getSize(size);
                bbox.getCenter(center);

                const parentPath: string[] = [];
                let parent = child.parent;

                while (parent) {
                    parentPath.unshift(parent.name || parent.type);
                    parent = parent.parent;
                }

                materials.set(key, {
                    modelName: tireRack.config.model_name,
                    rackName: tireRack.name,
                    modelUrl: tireRack.config.url,

                    meshName: child.name,
                    parentPath: parentPath.join(" > "),
                    materialName: material.name,

                    colorHex: material.color?.getHexString?.(),
                    colorCss: material.color ? `#${material.color.getHexString()}` : null,

                    bboxSizeX: size.x,
                    bboxSizeY: size.y,
                    bboxSizeZ: size.z,

                    bboxCenterX: center.x,
                    bboxCenterY: center.y,
                    bboxCenterZ: center.z,

                    roughness: material.roughness,
                    metalness: material.metalness,
                    opacity: material.opacity,
                    transparent: material.transparent,

                    map: material.map?.name || material.map?.source?.data?.src || null,
                    normalMap: material.normalMap?.name || material.normalMap?.source?.data?.src || null,
                    roughnessMap: material.roughnessMap?.name || material.roughnessMap?.source?.data?.src || null,
                    metalnessMap: material.metalnessMap?.name || material.metalnessMap?.source?.data?.src || null,
                });
            });
        });
    }, [tireRackScene, tireRack.id, tireRack.name, tireRack.config.model_name, tireRack.config.url]);

    const applyMaterialColors = useCallback(
      (object: Object3D, wireframe = false) => {
          const shouldPaintMetalWithoutSplitting = hasFaneraMaterial(object);

          object.traverse((child: Object3D) => {
              if (!(child instanceof Mesh) || !child.material) return;

              const materialList = Array.isArray(child.material) ? child.material : [child.material];

              materialList.forEach((rawMaterial) => {
                  const material = rawMaterial as MeshPhysicalMaterial;
                  const tireRackColor = colorsItems.tireRacks ?? colorsItems.brackets ?? "red";

                  if (material.name.includes("color")) {
                      material.color = new Color(colorsShade[tireRackColor]);
                      material.roughness = 0.8;
                      material.metalness = 0.5;
                      material.opacity = 1;
                  }

                  if (material.name.includes("metal")) {
                      const isSplit =
                        !shouldPaintMetalWithoutSplitting &&
                        !Array.isArray(child.material) &&
                        splitMetalMaterialByConnectedParts(
                          child,
                          material,
                          tireRack.config.model_name,
                          wireframe
                        );

                      if (isSplit) {
                          return;
                      }

                      material.color = new Color(0x797b7a);
                      material.roughness = 0.8;
                      material.metalness = 0.5;
                      material.opacity = 1;
                  }

                  material.transparent = true;
                  material.opacity = wireframe ? 0 : 1;
                  material.wireframe = wireframe;
              });
          });
      },
      [colorsShade, colorsItems.tireRacks, colorsItems.brackets, tireRack.config.model_name]
    );

    const tireRackObject = useMemo(() => {
        if (!tireRackScene) return null;
        const clonedScene = clone(tireRackScene);
        applyMaterialColors(clonedScene, isWireframe);
        return clonedScene;
    }, [tireRackScene, applyMaterialColors, isWireframe]);

    const handleClick = () => {
        if (!moveCameraEnabled.current) return;
        setCurrentEditItem(
          tireRack.id,
          false,
          groupRef.current,
          new Vector3(tireRack.position[0], tireRack.position[1], tireRack.position[2])
        );
    };

    const handleDragStart = async () => {
        moveCameraEnabled.current = false;
        setCursor("grabbing");
        setIsWireframe(true);

        if (groupRef.current) {
            decorBboxCollectionRef.current = getDecorBboxCollection(scene, groupRef.current);

            if (!bBoxRef.current) {
                const clonedObj = groupRef.current.clone();

                bBoxRef.current = createBboxMesh(clonedObj);

                const bbox = new Box3().setFromObject(bBoxRef.current);
                const size = new Vector3();
                bbox.getSize(size);

                bBoxRef.current.position.set(tireRack.position[0], tireRack.position[1], tireRack.position[2]);

                boxHelperRef.current = new BoxHelper(bBoxRef.current, 0xffff00);
                scene.add(boxHelperRef.current);
                scene.add(bBoxRef.current);
            }
        }
    };

    const handleDrag = (position: [number, number, number]) => {
        setCursor("grabbing");
        if (!visible) {
            setVisible(true);
        }

        if (bBoxRef.current && decorBboxCollectionRef.current) {
            const bbox = new Box3().setFromObject(bBoxRef.current);
            const size = new Vector3();
            bbox.getSize(size);

            const correctedPosition = calcCollisions({
                currentObject: bBoxRef.current,
                decor: {
                    surface: "floor",
                    side: "front",
                    translate: [0, 0, 0],
                    surfaceOffset: 0,
                },
                position,
                decorBboxCollection: decorBboxCollectionRef.current,
                roomSizes: { roomLength, roomHeight, roomDepth },
                offset: { x: 0, y: 0, z: 0 },
            });

            if (correctedPosition) {
                updateTireRack({
                    ...tireRack,
                    position: correctedPosition,
                });
            }
        }
    };

    const clearHelpers = () => {
        if (bBoxRef.current) {
            scene.remove(bBoxRef.current);
            bBoxRef.current = null;
        }
        if (boxHelperRef.current) {
            scene.remove(boxHelperRef.current);
            boxHelperRef.current = null;
        }
    };

    const handleDragEnd = () => {
        moveCameraEnabled.current = true;
        setVisible(false);
        setIsWireframe(false);
        setCursor("grab");
        clearHelpers();
        setCurrentEditItem(tireRack.id, false);
    };

    const handleEdit = () => {
        clearHelpers();
        moveCameraEnabled.current = true;
        setVisible(false);
        setIsWireframe(false);
        setCurrentEditItem(tireRack.id, false);
    };

    const handlePointerEnter = useCallback(() => {
        setCursor("grab");
    }, []);

    const handlePointerOut = useCallback(() => {
        setCursor("default");
    }, []);

    useEffect(() => {
        if (bBoxRef.current) {
            bBoxRef.current.position.set(tireRack.position[0], tireRack.position[1], tireRack.position[2]);
        }
        if (boxHelperRef.current) {
            boxHelperRef.current.update();
        }
    }, [tireRack.position]);

    useEffect(() => {
        return () => {
            clearHelpers();
        };
    }, []);

    const position: Object3D["position"] = useMemo(() => {
        return new Vector3(tireRack.position[0], 0, tireRack.position[2]);
    }, [tireRack.position]);

    return (
      <group name={`tireRack-${tireRack.id}`}>
          <UniversalDraggable
            decorRef={draggableRef}
            position={tireRack.position}
            onClick={handleClick}
            onDrag={handleDrag}
            onDragEnd={handleDragEnd}
            onDragStart={handleDragStart}
            onEdit={handleEdit}
          >
              <group
                ref={groupRef}
                position={position}
                rotation={tireRack.rotation}
                name={`tireRack-${tireRack.id}`}
                onPointerOut={handlePointerOut}
                onPointerEnter={handlePointerEnter}
              >
                  {tireRackObject && <primitive object={tireRackObject} />}
              </group>
          </UniversalDraggable>

          <TireRackDimensions
            tireRack={tireRack}
            visible={visible}
            bBoxTireRack={bBoxRef.current ? bBoxRef.current : undefined}
          />
      </group>
    );
});

export default TireRackObject;
